import { colourSolid } from '@/features/common/ui';
import { cn } from '@/lib/cn';

export interface LetterheadBlockProps {
  logoUrl?: string | null;
  /** The legal name, or the book's name when there is none - a document is never anonymous. */
  name: string;
  addressLines: string[];
  gstin?: string | null;
  /** "email · phone · website", already joined. */
  contact?: string;
  tagline?: string | null;
  /** A palette slot. The name and the rule beneath take this colour. */
  accent?: string | null;
  /** The settings preview renders smaller than the page; the geometry is the same. */
  compact?: boolean;
  testId?: string;
}

/**
 * The letterhead: logo on the left, the business on the right, a rule in the
 * accent colour beneath.
 *
 * ── ONE COMPONENT FOR THE PREVIEW AND THE PAGE ──
 * Settings shows "how it will print", and the only way that sentence is true
 * is if it is the same component the document renders. Two copies would drift
 * the first time somebody adjusted the logo's height on one of them, and the
 * person would find out from a customer.
 *
 * ── THE LOGO'S SIZE ──
 * Capped at 20mm tall and 60mm wide - the height of a Zoho letterhead's logo -
 * and scaled inside that box with `object-contain`, so a square mark and a
 * wide wordmark both fit without either being cropped. The `alt` is empty:
 * the name is printed beside it, and announcing "logo" before the name says
 * nothing the name does not.
 */
export function LetterheadBlock({
  logoUrl,
  name,
  addressLines,
  gstin,
  contact,
  tagline,
  accent,
  compact = false,
  testId,
}: LetterheadBlockProps) {
  const accentColour = colourSolid(accent);
  return (
    <div data-testid={testId}>
      {/* Side by side, as on paper. On a phone the page-size block stacks -
          logo, then the business - because at 360px the two halves left the
          email breaking mid-word; the compact preview stays side by side,
          since it is showing the paper's geometry, and wraps instead. */}
      <div className={cn('flex items-start justify-between', compact ? 'flex-wrap gap-3' : 'flex-col gap-3 sm:flex-row sm:gap-6')}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            data-testid={testId ? `${testId}-logo` : undefined}
            // On paper: a logo is drawn for a white page and disappears on
            // the dark theme's card. Invisible in the light theme, where the
            // card is already white.
            className={cn('shrink-0 rounded-sm bg-paper object-contain object-left-top', compact ? 'max-h-12 max-w-[36mm]' : 'max-h-20 max-w-[60mm]')}
          />
        ) : (
          // An empty flex item, so the block stays on the right where the
          // page expects it whether or not there is a logo.
          <span aria-hidden className="shrink-0" />
        )}
        <div className={cn('min-w-0 break-words leading-snug', compact ? 'ml-auto text-right text-[11px]' : 'text-xs sm:ml-auto sm:text-right sm:text-sm')}>
          <p
            className={cn('font-semibold', compact ? 'text-sm' : 'text-base sm:text-lg')}
            style={{ color: accentColour }}
            data-testid={testId ? `${testId}-name` : undefined}
          >
            {name}
          </p>
          {addressLines.map((line, index) => (
            <p key={`${index}-${line}`}>{line}</p>
          ))}
          {gstin ? <p className="font-medium">GSTIN {gstin}</p> : null}
          {contact ? <p className="text-muted-foreground">{contact}</p> : null}
          {tagline ? <p className="italic text-muted-foreground">{tagline}</p> : null}
        </div>
      </div>
      <div aria-hidden className={cn('rounded-full', compact ? 'mt-2 h-px' : 'mt-4 h-0.5')} style={{ backgroundColor: accentColour }} />
    </div>
  );
}
